import { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Typography, Box, Button, Chip } from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SessionContext from '../../context/Session';

const API_URL = process.env.REACT_APP_API_URL;

// Reasons that mean "this link doesn't point to a usable shotlist anymore" —
// deleted, never existed, or the underlying file is gone. `not_ready` is
// transient (export still processing) so it gets its own, less final, message.
const GONE_REASONS = new Set(['not_found', 'unavailable', 'invalid', 'server_error']);

const SharedExportViewer = () => {
  const { uuid } = useParams();
  const [sasUrl, setSasUrl] = useState(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const { setIsViewingPdf } = useContext(SessionContext);

  // Standard header/footer are suppressed only while a PDF is actually on
  // screen; the loading and expired/deleted-link states should look like any
  // other page, so restore them as soon as we leave the "sasUrl loaded" state.
  useEffect(() => {
    setIsViewingPdf(Boolean(sasUrl));
    return () => setIsViewingPdf(false);
  }, [sasUrl, setIsViewingPdf]);

  useEffect(() => {
    if (!uuid) {
      setError('Invalid link.');
      setReason('invalid');
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/view/${uuid}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.sasUrl) {
          setSasUrl(data.sasUrl);
        } else {
          setError(data?.error || 'This export is not available.');
          setReason(data?.reason || 'not_found');
        }
      })
      .catch(() => {
        setError('Unable to load export. Please try again later.');
        setReason('server_error');
      })
      .finally(() => setLoading(false));
  }, [uuid]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflow: 'hidden',
        backgroundColor: '#F7F6FF',
      }}
    >
      {/* Body */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={24} sx={{ color: '#7A64EB' }} />
            <Typography color="text.secondary">Loading export…</Typography>
          </Box>
        )}

        {!loading && error && GONE_REASONS.has(reason) && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              textAlign: 'center',
              px: 3,
              overflow: 'auto',
            }}
          >
            {/* Ghost preview of a shotlist, marked inactive */}
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 320 }}>
              <Box
                sx={{
                  filter: 'blur(2px)',
                  opacity: 0.5,
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  border: '1px solid #E3E0F7',
                  boxShadow: '0 8px 24px rgba(27, 19, 74, 0.08)',
                  p: 2.5,
                  textAlign: 'left',
                }}
              >
                <Box sx={{ height: 8, width: '55%', borderRadius: 1, backgroundColor: '#D8D4F5', mb: 2 }} />
                {[0, 1, 2].map((row) => (
                  <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#EDEBFB', flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 6, width: '70%', borderRadius: 1, backgroundColor: '#E3E0F7', mb: 0.75 }} />
                      <Box sx={{ height: 6, width: '40%', borderRadius: 1, backgroundColor: '#EDEBFB' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Chip
                  icon={<LinkOffIcon sx={{ color: '#fff !important', fontSize: 18 }} />}
                  label="LINK INACTIVE"
                  sx={{
                    backgroundColor: '#1B134A',
                    color: '#fff',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    px: 1,
                    py: 2.5,
                    boxShadow: '0 8px 20px rgba(27, 19, 74, 0.25)',
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ color: '#1B134A', fontWeight: 800, mb: 1 }}>
                This Shotlist has Left the Set
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                The link you followed is no longer active or the content has been moved. Please reach out to
                the original sender for a fresh link.
              </Typography>
            </Box>

            <Button
              variant="contained"
              href="https://www.shotkraft.com/?utm_source=viewer&utm_medium=shotlistexport"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                mt: 1,
                textTransform: 'none',
                borderRadius: 9999,
                backgroundColor: '#7A64EB',
                '&:hover': { backgroundColor: '#6a55e0' },
              }}
            >
              Create your own shotlist
            </Button>
          </Box>
        )}

        {!loading && error && !GONE_REASONS.has(reason) && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textAlign: 'center',
              px: 3,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check back in a moment.
            </Typography>
          </Box>
        )}

        {!loading && sasUrl && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                backgroundColor: '#fff',
                minHeight: 0,
              }}
            >
              <iframe
                title="Shotlist Export"
                src={sasUrl}
                width="100%"
                height="100%"
                style={{ display: 'block', border: 'none' }}
                allowFullScreen
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Sticky Footer CTA — always visible when PDF is loaded */}
      {!loading && sasUrl && (
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: '#1B134A',
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: { xs: 2, sm: 3 },
              borderRadius: 0,
              backgroundColor: '#1B134A',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700 }}>
                Build your own shotlists with ShotKraft
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                AI-powered shot lists for film and video production.
              </Typography>
            </Box>
            <Button
              variant="contained"
              href="https://www.shotkraft.com/?utm_source=viewer&utm_medium=shotlistexport"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: 'none',
                borderRadius: 9999,
                backgroundColor: '#7A64EB',
                '&:hover': { backgroundColor: '#6a55e0' },
                fontWeight: 600,
                px: 3,
                flexShrink: 0,
              }}
            >
              Get started free
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SharedExportViewer;
